import React, { useEffect, useReducer } from 'react';
import { FetchStatus, FetchType } from '../../../enums';
import * as store from './additional-learning.store';
import { useAppActions, useAppState } from '../../../overmind';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { AdditionalLearningPanel } from './panel/additional-learning.panel';
import { AdditionalLearningTypeEnum } from './additional-learning.models';
import { ConfirmationModalLayout } from '../../../components/modals/confirmation-modal/confirmation-modal.layout';
import { ReflectionPanel } from '../reflections/reflection.panel';
import { PagePath } from '../../../navigation/navigation.enums';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { Button, CascadingPanel, CascadingPanels, DeleteIcon, EditIcon, formatDate, IconButton, KeplerPoints, ListItemLayout, ListLayout, Modal, PageHeader, SearchBar, useCascadingPanelsConnector } from '@keplerco/core';
import { useDataSyncReaction } from '../../../library/hooks/useDataSyncReaction';
import layoutStyles from '../../../design/layout.module.css';
import classNames from 'classnames';
import styles from './additional-learning.module.css';

function mapLearningTypeEnum(value: AdditionalLearningTypeEnum) {
  if (value === AdditionalLearningTypeEnum.InHouse) {
    return 'In-house training';
  } else if (value === AdditionalLearningTypeEnum.ShortCourse) {
    return 'Short course';
  } else if (value === AdditionalLearningTypeEnum.Online) {
    return 'Online course';
  }

  return AdditionalLearningTypeEnum[value];
}

enum CascadingPanelIds {
  AdditionalLearning = 'AdditionalLearning',
  Reflection = 'Reflection',
}

export function AdditionalLearningPage(): JSX.Element {
  const actions = useAppActions();
  const { fetchState } = useAppState();

  const [state, dispatch] = useReducer(store.reducer, store.initialState);

  const { openPanelIds, next, close, closeAll } = useCascadingPanelsConnector();

  useDataSyncReaction(async () => {
    getData();
  });

  async function getData(type?: FetchType): Promise<void> {
    if (type !== undefined) actions.startLoader({ path: PagePath.learningJourneyAdditionalLearning, type: type });

    const data = await actions.getAdditionalLearningList(state.request);
    dispatch({ type: store.AdditionalLearningActionTypes.SetData, payload: data });

    if (type !== undefined) actions.stopLoader(PagePath.learningJourneyAdditionalLearning);
  }

  useEffect(() => {
    getData(!state.data ? FetchType.PageFetching : FetchType.Custom);
  }, [state.request?.searchTerm]);

  function closeReflections() {
    closeAll();
    getData(FetchType.PageFetching);
  }

  return (
    <React.Fragment>
      <PageLoaderLayer path={PagePath.learningJourneyAdditionalLearning}>
        <div className="wrapper stack" style={{ gap: 30 }}>
          <PageHeader
            breadcrumbs={[
              {
                name: 'Learning Journey',
                url: PagePath.learningJourneyBase,
              },
              {
                name: 'Additional Learning',
                url: window.location.pathname,
              },
            ]}
            title="Additional Learning"
            subtitle="Learning can take place anywhere, so if you've completed any in-person sessions, webinars, talks or learning from places outside of this platform, you can track it here. It's also important to reflect on your learning to validate your existing knowledge and skills."
            divider
          />

          <div className={layoutStyles.filtersGroup}>
            <SearchBar
              label='Search'
              loading={fetchState[PagePath.learningJourneyAdditionalLearning].type === FetchType.Custom && fetchState[PagePath.learningJourneyAdditionalLearning].status === FetchStatus.Active}
              onInput={event => {
                const searchTerm = (event.target as HTMLInputElement).value;
                dispatch({ type: store.AdditionalLearningActionTypes.SetRequest, payload: !!searchTerm ? { searchTerm } : {} });
              }}
            />

            {!!state.data?.length && <Button filled type="button" arrow={false} onClick={() => {
              dispatch({ type: store.AdditionalLearningActionTypes.SetAdditionalLearningToEdit, payload: { show: true } });
              next(CascadingPanelIds.AdditionalLearning);
            }}>
              Track Additional Learning
            </Button>}
          </div>

          {!state.data?.length ? (
            <EmptyState
              title="No additional learning has been logged yet"
              subtitle="Have you completed any in-person sessions, webinars, talks or learning from places outside of this platform?"
              buttonElements={[
                <Button key="Track Additional Learning" type="button" onClick={() => {
                  dispatch({ type: store.AdditionalLearningActionTypes.SetAdditionalLearningToEdit, payload: { show: true } });
                  next(CascadingPanelIds.AdditionalLearning);
                }}>
                  Track Additional Learning
                </Button>,
              ]}
            />
          ) : (
            <ListLayout>
              {state.data.map(item => {
                return (
                  <ListItemLayout key={item.slug}>
                    <div className={classNames('card', styles.card)}>
                      <div className={styles.delete}>
                        <IconButton iconType="fill" hoverTone="light" onClick={async () => dispatch({ type: store.AdditionalLearningActionTypes.SetAdditionalLearningToDelete, payload: item })}>
                          <DeleteIcon tone="default" />
                        </IconButton>
                      </div>

                      <div className={styles.content}>
                        <span className={classNames('body', styles.title)}>{item.name}</span>

                        <span className="body" style={{ fontWeight: 700 }}>
                          {mapLearningTypeEnum(item.type)}
                        </span>

                        <span className="body" style={{ color: 'var(--accent-2)' }}>
                          Added: {formatDate(new Date(item.completedOn))}
                        </span>

                        <span className={classNames('body', styles.keplerPoints)}>
                          <KeplerPoints trimWhiteSpace points={item.keplerPoints} /> Earned
                        </span>
                      </div>

                      <div className={styles.edit}>
                        <IconButton
                          iconType="stroke"
                          hoverTone="light"
                          onClick={async () => {
                            dispatch({ type: store.AdditionalLearningActionTypes.SetAdditionalLearningToEdit, payload: { show: true, data: item.slug } });
                            next(CascadingPanelIds.AdditionalLearning);
                          }}
                        >
                          <EditIcon tone="default" size={30} />
                        </IconButton>
                      </div>
                    </div>
                  </ListItemLayout>
                );
              })}
            </ListLayout>
          )}
        </div>
      </PageLoaderLayer>

      {/* TODO: unify additional learning and reflection panel combinations into a component */}
      <CascadingPanels
        openPanelIds={openPanelIds}
        onClosePanel={(id: string) => {
          switch (id as CascadingPanelIds) {
            case CascadingPanelIds.AdditionalLearning: {
              dispatch({ type: store.AdditionalLearningActionTypes.SetAdditionalLearningToEdit, payload: { show: false } });
              break;
            }

            case CascadingPanelIds.Reflection: {
              closeReflections();
              break;
            }
          }

          close(id);
        }}
      >
        <CascadingPanel id={CascadingPanelIds.AdditionalLearning}>
          <AdditionalLearningPanel
            slug={state.additionalLearningToEdit.data}
            path={PagePath.learningJourneyAdditionalLearning}
            onCancel={() => {
              dispatch({ type: store.AdditionalLearningActionTypes.SetAdditionalLearningToEdit, payload: { show: false } });
              closeAll();
            }}
            onSubmit={async (model, doReflection) => {
              actions.startLoader({ path: PagePath.learningJourneyAdditionalLearning, type: FetchType.Sending });

              const reflectionId = await actions.saveAdditionalLearning(model);

              if (!doReflection || !reflectionId) {
                getData(FetchType.PageFetching);
                closeAll();
                dispatch({ type: store.AdditionalLearningActionTypes.SetAdditionalLearningToEdit, payload: { show: false } });
                return;
              }

              dispatch({ type: store.AdditionalLearningActionTypes.SetReflectionId, payload: reflectionId });
              next(CascadingPanelIds.Reflection);
              dispatch({ type: store.AdditionalLearningActionTypes.SetAdditionalLearningToEdit, payload: { show: false } });

              actions.stopLoader(PagePath.learningJourneyAdditionalLearning);

              console.log('onSubmit');
            }}
          />
        </CascadingPanel>

        <CascadingPanel id={CascadingPanelIds.Reflection}>
          <ReflectionPanel reflectionId={state.reflectionId} path={PagePath.learningJourneyAdditionalLearning} onCancel={closeReflections} onSubmit={closeReflections} />
        </CascadingPanel>
      </CascadingPanels>

      <Modal open={!!state.additionalLearningToDelete} onClose={() => dispatch({ type: store.AdditionalLearningActionTypes.SetAdditionalLearningToDelete, payload: undefined })}>
        {!!state.additionalLearningToDelete && (
          <ConfirmationModalLayout
            title="Remove additional learning"
            subtitle={`"${mapLearningTypeEnum(state.additionalLearningToDelete.type)}: ${state.additionalLearningToDelete.name}"?`}
            cancelButtonText="No, cancel"
            onClickCancel={() => dispatch({ type: store.AdditionalLearningActionTypes.SetAdditionalLearningToDelete, payload: undefined })}
            submitButtonText="Yes, remove it"
            textAlign="left"
            onClickSubmit={async () => {
              if (!!state.additionalLearningToDelete) {
                actions.startLoader({ path: PagePath.learningJourneyAdditionalLearning, type: FetchType.Sending });
                await actions.deleteAdditionalLearning(state.additionalLearningToDelete.slug);
                getData(FetchType.PageFetching);
              }

              dispatch({ type: store.AdditionalLearningActionTypes.SetAdditionalLearningToDelete, payload: undefined });
            }}
          >
            <h3>
              Are you sure you want to remove{' '}
              <strong>
                {mapLearningTypeEnum(state.additionalLearningToDelete.type)}: {state.additionalLearningToDelete.name}
              </strong>
              ?
            </h3>
          </ConfirmationModalLayout>
        )}
      </Modal>
    </React.Fragment>
  );
}
